import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {
  useOrderIntakeSteps,
  useOrderIntakeStepper
} from '@hconnect/tuc-processes/orderIntake/wizard/useOrderIntakeSteps'
import {ArrowForward, ArrowBack, Delete} from '@mui/icons-material'

import {useMaterialVariationsState} from '../../shared/order-editor/hooks/useMaterialVariationsState'
import {ResetMaterialVariationsButton} from '../../shared/order-editor/components/buttons/ResetMaterialVariationsButton'
import {ContinueWithMaterialVariationsButton} from '../components/buttons/ContinueWithMaterialVariationsButton'
import {
  Button,
  Card,
  CardContent,
  Step,
  StepLabel,
  Stepper,
  Box,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import React, {useEffect, useRef, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {useStepConfigurations} from '../../shared/order-editor/hooks/useRegionConfig'
import {rmcStepFactories, RmcStepConfiguration} from '../config/rmcStepFactories'
import {RmcOrderIntakeStepType} from '../config/stepTypes'
import {useJobSite} from '../contexts/JobSiteContext'
import {useEnhancedLoadSpacingScenario} from '../hooks/useEnhancedLoadSpacingScenario'
import {formatJobSiteInfo} from '../utils/jobSiteUtils'
import {useStepperButtonState} from '../validation/useStepperButtonState'
import {useStepNavigation} from '../validation/useStepValidation'
import { OrderIntakeSlotValidityChecker } from '../components/slotValidity/OrderIntakeSlotValidityChecker'

/**
 * Props for the RMC Stepper Container
 */
interface RmcStepperContainerProps {
  onStepperComplete?: (formData: FieldsForm) => void
}

/**
 * Mapping of step types to label translation keys
 * This ensures all step labels are consistently mapped to translation keys
 */
const STEP_LABEL_KEYS: Record<RmcOrderIntakeStepType, string> = {
  [RmcOrderIntakeStepType.QUOTE]: 'orderIntake.rmc.stepper.labels.quote',
  [RmcOrderIntakeStepType.MATERIAL]: 'orderIntake.rmc.stepper.labels.material',
  [RmcOrderIntakeStepType.MIX_CHARACTERISTICS]: 'orderIntake.rmc.stepper.labels.materialVariations',
  [RmcOrderIntakeStepType.QUANTITY]: 'orderIntake.rmc.stepper.labels.quantity',
  [RmcOrderIntakeStepType.USAGE]: 'orderIntake.rmc.stepper.labels.usage',
  [RmcOrderIntakeStepType.UNLOADING_METHOD]: 'orderIntake.rmc.stepper.labels.unloading',
  [RmcOrderIntakeStepType.INTERVALS]: 'orderIntake.rmc.stepper.labels.spacing',
  [RmcOrderIntakeStepType.DATE]: 'orderIntake.rmc.stepper.labels.date',
  [RmcOrderIntakeStepType.TIME]: 'orderIntake.rmc.stepper.labels.time',
  [RmcOrderIntakeStepType.DETAILS]: 'orderIntake.rmc.stepper.labels.details'
}

/**
 * Mapping of step types to header translation keys
 * This ensures all step headers are consistently mapped to translation keys
 */
const STEP_HEADER_KEYS: Record<RmcOrderIntakeStepType, string> = {
  [RmcOrderIntakeStepType.QUOTE]: 'orderIntake.rmc.stepper.headers.quoteSelection',
  [RmcOrderIntakeStepType.MATERIAL]: 'orderIntake.rmc.stepper.headers.materialSelection',
  [RmcOrderIntakeStepType.MIX_CHARACTERISTICS]:
    'orderIntake.rmc.stepper.headers.materialVariations',
  [RmcOrderIntakeStepType.QUANTITY]: 'orderIntake.rmc.stepper.headers.quantityNeeded',
  [RmcOrderIntakeStepType.USAGE]: 'orderIntake.rmc.stepper.headers.concreteUsage',
  [RmcOrderIntakeStepType.UNLOADING_METHOD]: 'orderIntake.rmc.stepper.headers.unloadingMethod',
  [RmcOrderIntakeStepType.INTERVALS]: 'orderIntake.rmc.stepper.headers.deliveryIntervals',
  [RmcOrderIntakeStepType.DATE]: 'orderIntake.rmc.stepper.headers.deliveryDate',
  [RmcOrderIntakeStepType.TIME]: 'orderIntake.rmc.stepper.headers.deliveryTime',
  [RmcOrderIntakeStepType.DETAILS]: 'orderIntake.rmc.stepper.headers.additionalDetails'
}

// Stepper styling configuration
/**
 * Mapping of step types to label translation keys
 * This ensures all step labels are consistently mapped to translation keys
 */

/**
 * Container for the RMC order intake stepper.
 * Manages the step navigation and displays the current step component.
 */
// eslint-disable-next-line complexity
export const RmcStepperContainer: React.FC<RmcStepperContainerProps> = ({onStepperComplete}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const history = useHistory()

  // State for discard confirmation dialog
  const [discardDialogOpen, setDiscardDialogOpen] = React.useState(false)

  // State to track if user clicked "Continue with material variations" button
  const [shouldShowMaterialVariations, setShouldShowMaterialVariations] = React.useState(false)

  // Ref for the stepper container to enable scrolling to active step
  const stepperContainerRef = useRef<HTMLDivElement>(null)

  // Get step configurations from hook
  const stepConfigs = useStepConfigurations()

  // Consistent styles across all screen sizes - no screen-specific styles

  // Define stepper styles inside useMemo to avoid re-creation on each render
  const stepperStyles = React.useMemo(() => {
    return {
      '& .MuiStepConnector-root': {
        top: 20,
        '& .MuiStepConnector-line': {
          borderColor: '#D1D9E0',
          borderTopWidth: 1
        }
      },
      '& .MuiStep-root': { minWidth: '88px' },
      '& .MuiStepLabel-root': { color: '#FFFFFF', fontSize: '12px' },
      '& .MuiStepLabel-label': {
        color: '#FFFFFF !important',
        fontSize: '12px',
        fontWeight: 400,
        marginTop: '8px',
        textAlign: 'center',
        '&.Mui-active': {fontWeight: 600},
        '&.Mui-completed': {fontWeight: 600}
      },
      '& .MuiStepIcon-root': {
        width: 40,
        height: 40,
        color: '#E6F0FB',
        border: '1px solid #D1D9E0',
        borderRadius: '50%',
        '&.Mui-active': {color: '#80B4E9'},
        '&.Mui-completed': {color: '#00AB50', backgroundColor: '#E6F0FB'},
        '& .MuiStepIcon-text': {fill: '#52667A', fontSize: '8px', fontWeight: 500},
        '&.Mui-active .MuiStepIcon-text': {fill: '#FFFFFF', fontWeight: 600, fontSize: '8px'}
      },
      // Additional optimization
      '@media (max-width: 240px)': {
        '& .MuiStepConnector-root': {
          top: 12
        },
        '& .MuiStep-root': {
          minWidth: '60px'
        },
        '& .MuiStepLabel-label': {
          fontSize: '8px !important',
          marginTop: '4px'
        },
        '& .MuiStepIcon-root': {
          width: 24,
          height: 24,
          '& .MuiStepIcon-text': {
            fontSize: '6px'
          }
        }
      }
    }
  }, [])

  // Create step configuration for the stepper based on dynamic configurations from hooks
  const stepConfiguration: RmcStepConfiguration = React.useMemo(() => {
    return {
      isRmcOrder: true,
      // Build enabledSteps map based on step configurations
      enabledSteps: stepConfigs.reduce(
        (map, stepConfig) => {
          // Special handling for MIX_CHARACTERISTICS step - only enable if button was clicked
          if (stepConfig.name === RmcOrderIntakeStepType.MIX_CHARACTERISTICS) {
            map[stepConfig.name] = shouldShowMaterialVariations
          } else {
            map[stepConfig.name] = stepConfig.isEnabled
          }
          return map
        },
        {} as Record<RmcOrderIntakeStepType, boolean>
      ),
      // Build step order map based on step configurations
      stepOrder: stepConfigs.reduce(
        (map, stepConfig) => {
          map[stepConfig.name] = stepConfig.order
          return map
        },
        {} as Record<RmcOrderIntakeStepType, number>
      )
    }
  }, [stepConfigs, shouldShowMaterialVariations])

  // Use the proper step factories and stepper hooks
  const steps = useOrderIntakeSteps(rmcStepFactories, stepConfiguration)

  // Using step keys directly for mapping between steps and their types

  // Sort steps based on order using the step keys directly
  const sortedSteps = React.useMemo(() => {
    return [...steps].sort((a, b) => {
      // Use the key property of each step for sorting
      const stepTypeA = a.key as RmcOrderIntakeStepType
      const stepTypeB = b.key as RmcOrderIntakeStepType

      // If we can't determine the step type, use a high number
      // Use explicit undefined check to handle order value of 0 correctly
      const orderA =
        stepTypeA && stepConfiguration.stepOrder?.[stepTypeA] !== undefined
          ? stepConfiguration.stepOrder[stepTypeA]
          : 999
      const orderB =
        stepTypeB && stepConfiguration.stepOrder?.[stepTypeB] !== undefined
          ? stepConfiguration.stepOrder[stepTypeB]
          : 999

      return orderA - orderB
    })
  }, [steps, stepConfiguration.stepOrder])

  const {currentStep, currentStepIndex, goToNextStep, goToPreviousStep, setCurrentStep} =
    useOrderIntakeStepper(sortedSteps)
  const {jobSite} = useJobSite()

  const timeStepIndex = React.useMemo(
    () => sortedSteps.findIndex((step) => step.key === RmcOrderIntakeStepType.TIME),
    [sortedSteps]
  )

  const handleChangeStartTime = useCallback(() => {
    if (timeStepIndex >= 0) {
      setCurrentStep(timeStepIndex)
    }
  }, [setCurrentStep, timeStepIndex])

  // Get form context to access form data
  const { getValues } = useOrderProcessFormContext()

  // Get selected material to check if it has variations (for material step)
  const { selectedMaterial, selectedMaterialHasVariations, resetMaterialVariations } = useMix()
  const isOnMaterialStep =
    (currentStep.key as RmcOrderIntakeStepType) === RmcOrderIntakeStepType.MATERIAL
  const canConfigureMix = isOnMaterialStep && selectedMaterialHasVariations

  // Get material variations state for reset functionality
  const { shouldShowResetButton } = useMaterialVariationsState(currentStep.key)

  // Reset shouldShowMaterialVariations when material changes or loses variations
  React.useEffect(() => {
    if (!selectedMaterialHasVariations) {
      setShouldShowMaterialVariations(false)
    }
  }, [selectedMaterialHasVariations])

  // Get enhanced load spacing scenario for dynamic header logic with country features
  const scenario = useEnhancedLoadSpacingScenario()

  /**
   * Get label for a specific step type
   * This ensures consistent labels across the stepper
   * For intervals step, dynamically select the label based on the enhanced scenario
   */
  const getStepLabel = React.useCallback(
    (stepType: RmcOrderIntakeStepType): string => {
      // Special case for intervals step - use dynamic text based on enhanced scenario
      if (stepType === RmcOrderIntakeStepType.INTERVALS) {
        // If spacing is present, always show "spacing" label
        if (scenario.loadSpace) {
          return t('orderIntake.rmc.stepper.labels.spacing')
        }
        // If only timeOnJobSite is available (no spacing), show time on job site label
        else if (scenario.timeOnJobSite) {
          return t('orderIntake.rmc.stepper.labels.timeOnJobSite')
        }
        // Fallback to default spacing label
        return t('orderIntake.rmc.stepper.labels.spacing')
      }

      // Default to the standard label for this step type
      return t(STEP_LABEL_KEYS[stepType])
    },
    [scenario.loadSpace, scenario.timeOnJobSite, t]
  )

  /**
   * Get the appropriate header text for the current step
   * For intervals step, dynamically select the header based on the enhanced scenario
   */
  const getStepHeader = React.useCallback(
    (stepType: RmcOrderIntakeStepType): string => {
      // Special case for intervals step - use dynamic text based on enhanced scenario
      if (stepType === RmcOrderIntakeStepType.INTERVALS) {
        // If spacing is present, show spacing configuration header
        if (scenario.loadSpace) {
          return t('orderIntake.rmc.stepper.headers.spacingConfiguration')
        }
        // If only timeOnJobSite is available (no spacing), show job site time header
        else if (scenario.timeOnJobSite) {
          return t('orderIntake.rmc.stepper.headers.jobSiteTime')
        }
      }

      // Default to the standard header for this step type
      return t(STEP_HEADER_KEYS[stepType])
    },
    [scenario.loadSpace, scenario.timeOnJobSite, t]
  )

  // Check if we're on the final step
  const isLastStep = currentStepIndex === steps.length - 1

  // Custom navigation handler for final step
  const handleFinalStepComplete = React.useCallback(() => {
    if (onStepperComplete) {
      const formData = getValues()
      onStepperComplete(formData)
    }
  }, [onStepperComplete, getValues])

  // Use clean validation hooks following React best practices
  const {
    handleNextStep: baseHandleNextStep,
    handlePreviousStep: baseHandlePreviousStep,
    canProceedToNext
  } = useStepNavigation(
    currentStep,
    isLastStep ? handleFinalStepComplete : goToNextStep,
    goToPreviousStep
  )

  // Custom navigation handlers with analytics tracking
  const handleNextStep = useCallback(async () => {
    const currentStepType = currentStep.key as RmcOrderIntakeStepType

    // If on Material step, ensure Material Variations is disabled when using regular Continue button
    if (currentStepType === RmcOrderIntakeStepType.MATERIAL) {
      setShouldShowMaterialVariations(false)
    }

    // Track step started event for the current step
    trackEvent('rmcStepStarted', {
      stepType: currentStepType,
      stepNumber: currentStepIndex + 1,
      businessLine: 'RMC'
    })

    try {
      await baseHandleNextStep()

      // If navigation was successful, track step completed
      trackEvent('rmcStepCompleted', {
        stepType: currentStepType,
        stepNumber: currentStepIndex + 1,
        businessLine: 'RMC'
      })
    } catch (error) {
      // Track validation error if step navigation failed
      trackEvent('rmcStepValidationError', {
        stepType: currentStepType,
        stepNumber: currentStepIndex + 1,
        businessLine: 'RMC',
        error: error instanceof Error ? error.message : 'Unknown validation error'
      })
    }
  }, [currentStep, currentStepIndex, baseHandleNextStep])

  const handlePreviousStep = useCallback(() => {
    const currentStepType = currentStep.key as RmcOrderIntakeStepType

    // Track step back navigation
    trackEvent('rmcStepBack', {
      stepType: currentStepType,
      stepNumber: currentStepIndex + 1,
      businessLine: 'RMC'
    })

    baseHandlePreviousStep()
  }, [currentStep, currentStepIndex, baseHandlePreviousStep])

  // Get button state and styling, with custom text for final step
  const {styles, isDisabled} = useStepperButtonState(canProceedToNext, false)

  // Custom button text logic
  const buttonText = React.useMemo(() => {
    if (isLastStep) {
      return canProceedToNext
        ? t('orderIntake.rmc.stepper.buttons.completeOrder')
        : t('orderIntake.rmc.stepper.buttons.completeRequiredFields')
    }
    return canProceedToNext
      ? t('orderIntake.rmc.stepper.buttons.continue')
      : t('orderIntake.rmc.stepper.buttons.selectAndContinue')
  }, [isLastStep, canProceedToNext, t])

  // No automatic vertical scrolling for step content

  // Effect to scroll to active step when it changes
  useEffect(() => {
    if (stepperContainerRef.current) {
      // Find all step elements
      const stepElements = stepperContainerRef.current.querySelectorAll('.MuiStep-root')

      // If we have steps and the current step index is valid
      if (
        stepElements &&
        stepElements.length > 0 &&
        currentStepIndex >= 0 &&
        currentStepIndex < stepElements.length
      ) {
        const activeStep = stepElements[currentStepIndex] as HTMLElement

        if (activeStep) {
          // Calculate the position to scroll to (center the active step in the container)
          const containerWidth = stepperContainerRef.current.offsetWidth
          const activeStepLeft = activeStep.offsetLeft
          const activeStepWidth = activeStep.offsetWidth

          // Calculate position to center the active step
          const scrollPosition = activeStepLeft - containerWidth / 2 + activeStepWidth / 2

          // Smooth scroll to the active step
          stepperContainerRef.current.scrollTo({
            left: Math.max(0, scrollPosition),
            behavior: 'smooth'
          })
        }
      }
    }
  }, [currentStepIndex]) // Re-run when current step changes

  // Handler for opening discard confirmation dialog
  const handleOpenDiscardDialog = () => {
    setDiscardDialogOpen(true)
  }

  // Handler for closing discard confirmation dialog
  const handleCloseDiscardDialog = () => {
    setDiscardDialogOpen(false)
  }

  // Handler for confirming discard action
  const handleConfirmDiscard = () => {
    // Track order discard event
    trackEvent('rmcOrderDiscarded', {
      stepType: currentStep.key as RmcOrderIntakeStepType,
      stepNumber: currentStepIndex + 1,
      businessLine: 'RMC'
    })

    setDiscardDialogOpen(false)
    // Navigate back to the overview page
    history.push('/overview')
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        height: {xs: '100vh', sm: 'auto'},
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: {
          xs: 1,
          sm: 3,
          md: 4
        },
        boxSizing: 'border-box',
        overflow: { xs: 'hidden', sm: 'visible' },
        overflowX: 'hidden',
        // Custom breakpoints for ultra-small screens
        '@media (max-width: 320px)': {
          p: 0.75,
          minHeight: '100vh'
        },
        '@media (max-width: 240px)': {
          p: 0.25,
          minHeight: '100vh',
          height: '100vh',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y'
        }
      }}
    >
      <OrderIntakeSlotValidityChecker
        currentStepKey={currentStep.key as string}
        onChangeStartTime={handleChangeStartTime}
      />
      {/* Material-UI Stepper with custom styling */}
      <Box
        ref={stepperContainerRef}
        sx={{
          mb: 2,
          width: '100%',
          maxWidth: '900px',
          overflowX: 'auto',
          pb: 1,
          flexShrink: 0,
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          // Custom breakpoints for ultra-small screens
          '@media (max-width: 240px)': {
            mb: 1,
            pb: 0.5,
            maxHeight: '60px'
          }
        }}
      >
        <Stepper
          activeStep={currentStepIndex}
          alternativeLabel={true}
          orientation="horizontal"
          data-test-id="rmc-order-stepper"
          sx={{
            ...stepperStyles,
            minWidth: isSmallScreen ? '600px' : 'auto', // Ensure minimum width on small screens to allow scrolling
            '@media (max-width: 240px)': {
              minWidth: '480px', // Smaller minimum width
              '& .MuiStep-root': {
                minWidth: '60px' // Smaller step width
              },
              '& .MuiStepLabel-label': {
                fontSize: '8px !important',
                marginTop: '4px'
              },
              '& .MuiStepIcon-root': {
                width: 24,
                height: 24,
                '& .MuiStepIcon-text': {
                  fontSize: '6px'
                }
              }
            }
          }}
        >
          {sortedSteps.map((step, index) => (
            <Step key={index} data-test-id={`stepper-step-${step.key}`}>
              <StepLabel>{getStepLabel(step.key as RmcOrderIntakeStepType)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main content container */}
      <Card
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '600px',
            md: '710px'
          },
          height: {
            xs: 'calc(100vh - 120px)', // Reduced space for ultra-small screens
            sm: '600px',
            md: '700px'
          },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: {xs: '4px', sm: '8px'},
          boxShadow: '0px 2px 2px 0px rgba(31,73,94,0.08)',
          mb: {xs: 1, sm: 0},
          // Custom breakpoints for ultra-small screens
          '@media (max-width: 320px)': {
            height: 'calc(100vh - 100px)',
            borderRadius: '2px'
          },
          '@media (max-width: 240px)': {
            height: 'calc(100vh - 70px)',
            borderRadius: '1px',
            mb: 0
          }
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.5,
              sm: 3,
              md: 4
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overflowX: 'hidden',
            maxWidth: '100%',
            wordWrap: 'break-word',
            // Custom breakpoints for ultra-small screens
            '@media (max-width: 320px)': {
              p: 1
            },
            '@media (max-width: 240px)': {
              p: 0.5
            }
          }}
        >
          {/* Step content header */}
          <Box
            sx={{
              mb: {
                xs: 2,
                sm: 3,
                md: 4
              },
              // Even smaller spacing for ultra-small screens
              '@media (max-width: 320px)': {
                mb: 1.5
              },
              '@media (max-width: 240px)': {
                mb: 0.75
              }
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: '#29333D',
                fontWeight: 600,
                fontSize: {
                  xs: '16px',
                  sm: '20px'
                },
                lineHeight: {
                  xs: '20px',
                  sm: '24px'
                },
                mb: 0.5,
                // Custom breakpoints for ultra-small screens
                '@media (max-width: 320px)': {
                  fontSize: '14px',
                  lineHeight: '18px'
                },
                '@media (max-width: 240px)': {
                  fontSize: '11px',
                  lineHeight: '14px',
                  mb: 0.25
                }
              }}
            >
              {getStepHeader(currentStep.key as RmcOrderIntakeStepType)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#52667A',
                fontSize: {
                  xs: '10px',
                  sm: '12px'
                },
                lineHeight: {
                  xs: '14px',
                  sm: '16px'
                },
                // Custom breakpoints for ultra-small screens
                '@media (max-width: 320px)': {
                  fontSize: '9px',
                  lineHeight: '12px'
                },
                '@media (max-width: 240px)': {
                  fontSize: '7px',
                  lineHeight: '9px'
                }
              }}
            >
              {formatJobSiteInfo(jobSite)}
            </Typography>
          </Box>

          {/* Step content */}
          <Box
            sx={{
              flex: 1,
              mb: {
                xs: 1.5,
                sm: 3
              },
              overflowY: 'auto',
              overflowX: 'hidden',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              // Custom breakpoints for ultra-small screens
              '@media (max-width: 320px)': {
                mb: 1
              },
              '@media (max-width: 240px)': {
                mb: 0.5,
                WebkitOverflowScrolling: 'touch',
                maxHeight: 'calc(100vh - 200px)'
              }
            }}
          >
            <currentStep.Component onSuccess={goToNextStep} />
          </Box>

          {/* Navigation buttons */}
          <Stack
            spacing={{
              xs: 1,
              sm: 2
            }}
            sx={{
              // Even smaller spacing for ultra-small screens
              '@media (max-width: 320px)': {
                gap: 0.75
              },
              '@media (max-width: 240px)': {
                gap: 0.5
              }
            }}
          >
            {/* Continue with material variations button - only for material step with variations */}
            {canConfigureMix && (
              <ContinueWithMaterialVariationsButton
                materialId={selectedMaterial?.materialId ?? undefined}
                onContinue={() => {
                  setShouldShowMaterialVariations(true)
                  // Use setTimeout to allow state update to propagate before navigation
                  setTimeout(() => goToNextStep(), 0)
                }}
              />
            )}

            {/* Reset to default values button - only for material variations step with configured values */}
            {shouldShowResetButton && (
              <ResetMaterialVariationsButton
                onReset={() => resetMaterialVariations?.()}
                testId="reset-material-variations-button"
                variant="responsive"
              />
            )}

            {/* Main navigation row - Back and Continue */}
            {currentStepIndex > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: {
                    xs: 0.75,
                    sm: 2
                  },
                  alignItems: 'center',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  width: '100%',
                  // Custom breakpoints for ultra-small screens
                  '@media (max-width: 320px)': {
                    gap: 0.5,
                    flexDirection: 'column'
                  }
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBack />}
                  onClick={handlePreviousStep}
                  data-test-id="stepper-back-button"
                  size={isSmallScreen ? 'small' : 'medium'}
                  sx={{
                    ...styles.backButton,
                    width: isSmallScreen ? '100%' : 'auto',
                    order: isSmallScreen ? 2 : 1,
                    // Custom breakpoints for ultra-small screens
                    '@media (max-width: 320px)': {
                      fontSize: '13px',
                      padding: '6px 12px',
                      minHeight: '36px'
                    },
                    '@media (max-width: 240px)': {
                      fontSize: '10px',
                      padding: '3px 6px',
                      minHeight: '28px'
                    }
                  }}
                >
                  {t('orderIntake.rmc.stepper.buttons.back')}
                </Button>

                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleNextStep}
                  disabled={isDisabled}
                  data-test-id="stepper-continue-button"
                  size={isSmallScreen ? 'small' : 'medium'}
                  sx={{
                    ...styles.continueButton,
                    width: isSmallScreen ? '100%' : 'auto',
                    order: isSmallScreen ? 1 : 2,
                    // Custom breakpoints for ultra-small screens
                    '@media (max-width: 320px)': {
                      fontSize: '13px',
                      padding: '8px 16px',
                      minHeight: '40px'
                    },
                    '@media (max-width: 240px)': {
                      fontSize: '10px',
                      padding: '4px 8px',
                      minHeight: '32px'
                    }
                  }}
                >
                  {buttonText}
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNextStep}
                disabled={isDisabled}
                data-test-id="stepper-continue-button"
                fullWidth
                size={isSmallScreen ? 'small' : 'medium'}
                sx={{
                  ...styles.continueButton,
                  // Custom breakpoints for ultra-small screens
                  '@media (max-width: 320px)': {
                    fontSize: '13px',
                    padding: '8px 16px',
                    minHeight: '40px'
                  },
                  '@media (max-width: 240px)': {
                    fontSize: '10px',
                    padding: '4px 8px',
                    minHeight: '32px'
                  }
                }}
              >
                {buttonText}
              </Button>
            )}

            {/* Discard button centered below */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: {
                  xs: 0.75,
                  sm: 2
                },
                // Custom breakpoints for ultra-small screens
                '@media (max-width: 320px)': {
                  mt: 0.5
                }
              }}
            >
              <Button
                variant="text"
                startIcon={<Delete />}
                data-test-id="stepper-discard-button"
                sx={{
                  ...styles.discardButton,
                  // Custom breakpoints for ultra-small screens
                  '@media (max-width: 320px)': {
                    fontSize: '12px',
                    padding: '4px 8px',
                    minHeight: '32px'
                  },
                  '@media (max-width: 240px)': {
                    fontSize: '9px',
                    padding: '2px 4px',
                    minHeight: '24px'
                  }
                }}
                size={isSmallScreen ? 'small' : 'medium'}
                onClick={handleOpenDiscardDialog}
              >
                {t('orderIntake.rmc.stepper.buttons.discard')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Discard confirmation dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={handleCloseDiscardDialog}
        aria-labelledby="discard-confirmation-dialog-title"
        aria-describedby="discard-confirmation-dialog-description"
        sx={{
          // Ensure dialog fits ultra-small screens
          '& .MuiDialog-paper': {
            margin: {
              xs: 1,
              sm: 2
            },
            maxWidth: {
              xs: 'calc(100vw - 16px)',
              sm: '500px'
            },
            width: {
              xs: 'calc(100vw - 16px)',
              sm: 'auto'
            },
            // Ultra-small screen optimizations
            '@media (max-width: 320px)': {
              margin: 0.5,
              maxWidth: 'calc(100vw - 8px)',
              width: 'calc(100vw - 8px)'
            },
            '@media (max-width: 240px)': {
              margin: 0.25,
              maxWidth: 'calc(100vw - 4px)',
              width: 'calc(100vw - 4px)',
              fontSize: '12px'
            }
          }
        }}
      >
        <DialogTitle
          id="discard-confirmation-dialog-title"
          sx={{
            fontSize: {
              xs: '16px',
              sm: '20px'
            },
            padding: {
              xs: 2,
              sm: 3
            },
            // Ultra-small screen optimizations
            '@media (max-width: 240px)': {
              fontSize: '12px',
              padding: 1
            }
          }}
        >
          {t('orderIntake.rmc.stepper.dialogs.discardConfirmation.title')}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: {
              xs: 2,
              sm: 3
            },
            // Ultra-small screen optimizations
            '@media (max-width: 240px)': {
              padding: 1
            }
          }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontSize: {
                xs: '12px',
                sm: '14px'
              },
              // Ultra-small screen optimizations
              '@media (max-width: 240px)': {
                fontSize: '10px'
              }
            }}
          >
            {t('orderIntake.rmc.stepper.dialogs.discardConfirmation.message')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: {
              xs: 2,
              sm: 3
            },
            gap: {
              xs: 1,
              sm: 2
            },
            // Ultra-small screen optimizations
            '@media (max-width: 240px)': {
              padding: 1,
              gap: 0.5,
              flexDirection: 'column'
            }
          }}
        >
          <Button
            onClick={handleCloseDiscardDialog}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            sx={{
              // Ultra-small screen optimizations
              '@media (max-width: 240px)': {
                fontSize: '10px',
                width: '100%'
              }
            }}
          >
            {t('orderIntake.rmc.stepper.dialogs.discardConfirmation.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            color="error"
            variant="contained"
            size={isSmallScreen ? 'small' : 'medium'}
            sx={{
              // Ultra-small screen optimizations
              '@media (max-width: 240px)': {
                fontSize: '10px',
                width: '100%'
              }
            }}
          >
            {t('orderIntake.rmc.stepper.dialogs.discardConfirmation.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
